import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { createContext, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { type ConnectionChangeEvent, StreamChat } from 'stream-chat';

import { getAccountSettings } from '@/data/account/selectors/accountSettings';
import { getAuthUser } from '@/shared/auth/selectors';
import { usePrevious } from '@/shared/hooks/usePrevious';
import { useWiwDispatch, useWiwSelector } from '@/store';

import { WORKCHAT_ERRORS } from '@/workchat/constants';
import { customUploadConfig } from '@/workchat/helpers/streamClientHelpers';
import { authenticateWorkchat } from '@/workchat/v2/store/auth/authActions';
import { clearToken } from '@/workchat/v2/store/auth/authReducer';
import { clearInitError, setConnected, setDisconnected, setInitError } from '@/workchat/v2/store/reducer';
import {
  getWorkchatAuthTokenStream,
  getWorkchatInitError,
  getWorkchatShow,
  WorkChatTokenSource,
} from '@/workchat/v2/store/selectors';

type WorkChatClientContextApi = {
  client?: StreamChat;
  init?: () => void;
};

const StreamContext = createContext<WorkChatClientContextApi>({});

enum StreamEvents {
  ConnectionChanged = 'connection.changed',
}

const AUTH_RETRY_DELAY = 5000;
const MAX_INIT_ATTEMPTS = 3;

let skipTokenCache = false;

export const StreamProvider = ({ children }: PropsWithChildren<any>) => {
  const dispatch = useWiwDispatch();
  const user = useWiwSelector(getAuthUser);
  const accountSettings = useWiwSelector(getAccountSettings);
  const token = useWiwSelector(getWorkchatAuthTokenStream);
  const initError = useWiwSelector(getWorkchatInitError);

  const currWorkchatShow = useWiwSelector(getWorkchatShow);
  const prevWorkchatShow = usePrevious(currWorkchatShow);

  const [initRetryCount, setInitRetryCount] = useState<number>(0);
  const [connectionEventListener, setConnectionEventListener] = useState<{ unsubscribe: () => void }>();
  const [client, setClient] = useState<StreamChat>();

  const enableWorkchat = accountSettings.workchat.enabled && user.id > 0;
  const streamClient = StreamChat.getInstance(CONFIG.STREAM_KEY);

  const authPromiseRef = useRef<Promise<any>>();
  const timeoutRef = useRef<number | undefined>();

  /**
   * Method: Handle initialisation errors via Auth or Stream Client initialisation.
   */
  const handleInitError = (err: any, errorTag: WORKCHAT_ERRORS) => {
    Sentry.captureException(err, {
      tags: {
        workchatVersion: 3,
        workchatError: errorTag,
      },
    });

    // Retry for up to 3 times, if it fails on the 3rd attempt, set initError
    if ((initRetryCount + 1) % MAX_INIT_ATTEMPTS === 0) {
      return dispatch(setInitError('Client was unable to init!'));
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(
      () => setInitRetryCount(count => count + 1),
      // Retry auth every 10 seconds
      AUTH_RETRY_DELAY,
    );
  };

  const tokenProvider = () => {
    if (token && !skipTokenCache) {
      const decoded = jwtDecode(token).exp;
      // Server exp is in seconds, convert to ms
      if (decoded && decoded * 1000 > new Date().getTime()) {
        return Promise.resolve(token);
      }
    }

    return dispatch(authenticateWorkchat({ v2: true }))
      .unwrap()
      .then(resp => {
        localStorage.setItem(WorkChatTokenSource.Stream, JSON.stringify({ token: resp.token, user_id: user.id }));
        return resp.token;
      });
  };

  /**
   * Method: Initializes the Stream Client.
   */
  const init = async () => {
    if (authPromiseRef.current) {
      return;
    }

    dispatch(clearInitError());

    try {
      authPromiseRef.current = streamClient.connectUser({ id: `${user.id}` }, tokenProvider);

      await authPromiseRef.current;

      setConnectionEventListener(
        streamClient.on(StreamEvents.ConnectionChanged, (event: ConnectionChangeEvent) => {
          if (event.online) {
            dispatch(setConnected());
          } else {
            dispatch(setDisconnected());
          }
        }),
      );

      // Customize Attachment CDN
      streamClient.setMessageComposerSetupFunction(({ composer }) => {
        composer.updateConfig({
          attachments: {
            doUploadRequest: customUploadConfig,
          },
        });
      });

      dispatch(setConnected());
      dispatch(clearInitError());
      setInitRetryCount(0);
      setClient(streamClient);
      skipTokenCache = false;
    } catch (error: any) {
      // For the sake of simplicity, we'll retrieve a new token everytime we have Stream connection errors
      dispatch(clearToken());
      localStorage.removeItem(WorkChatTokenSource.Stream);
      skipTokenCache = true;
      handleInitError(error, WORKCHAT_ERRORS.failedToInitClient);
    } finally {
      authPromiseRef.current = undefined;
    }
  };

  /**
   * Effect: Cleanup on dismount.
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (authPromiseRef.current) {
        authPromiseRef.current = undefined;
      }
    };
  }, []);

  /**
   * Effect: Cleanup listeners
   */
  useEffect(() => {
    return () => {
      if (connectionEventListener) {
        connectionEventListener.unsubscribe();
      }
    };
  }, [connectionEventListener]);

  /**
   * Effect: Bootstrap WorkChat initialization process.
   */
  useEffect(() => {
    if (!enableWorkchat) {
      return;
    }

    if (!client || initRetryCount > 0) {
      init();
    }
  }, [client, initRetryCount, enableWorkchat]);

  /**
   * Effect: Cleanup previous Client
   */
  useEffect(() => {
    return () => {
      client?.disconnectUser();
    };
  }, [client]);

  /**
   * Effect: Handle retry on show when there's been a Init error.
   */
  useEffect(() => {
    if (initError && !prevWorkchatShow && currWorkchatShow) {
      setInitRetryCount(count => count + 1);
    }
  }, [initError, currWorkchatShow, prevWorkchatShow]);

  return (
    <StreamContext.Provider value={{ client, init: () => setInitRetryCount(count => count + 1) }}>
      {children}
    </StreamContext.Provider>
  );
};

export const useStreamProvider = () => {
  const { client, init } = useContext(StreamContext);

  return { client, init };
};
